import { Text } from "@asayinc/component-library";

import { PageContainer } from "src/components/containers/PageContainer/PageContainer";

import { SecondaryTabs, TableV2 as Table } from "@asayinc/component-library";
import { Stack } from "@mui/material";
import {
  History,
  HistoryOutlined,
  Verified,
  VerifiedOutlined,
  WatchLater,
  WatchLaterOutlined,
  DownloadingOutlined,
  Downloading,
} from "@mui/icons-material";
import { useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { ROW_OPTIONS } from "src/constants/options/rowOptions";
import { useTablePagination } from "src/hooks/useTablePagination";
import { useClassActionEventsQuery } from "src/store/classActionEvent";
import { getColumnData, getColumns, getRowData } from "./utils";
import { IClassActionEvent } from "src/store/classActionEvent/types";
import { TABLE_SEARCH_PARAMS } from "src/constants/tableSearchParams";
import { ScrollContainer } from "src/components/containers/ScrollContainer";

export type ClassActionEventsView =
  | "upcoming"
  | "active"
  | "recently-completed"
  | "historical";

export const CLASS_ACTION_EVENTS_VIEW: {
  [key: string]: ClassActionEventsView;
} = {
  UPCOMING: "upcoming",
  ACTIVE: "active",
  RECENTLY_COMPLETED: "recently-completed",
  HISTORICAL: "historical",
};

export function ClassActionEvents() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const view = (searchParams.get(TABLE_SEARCH_PARAMS.VIEW) ||
    CLASS_ACTION_EVENTS_VIEW.UPCOMING) as ClassActionEventsView;

  const {
    getSort,
    offset,
    limit,
    ordering,
    onChangePage,
    onChangeRows,
    onOrderingChange,
  } = useTablePagination();
  const { data, isFetching, isLoading } = useClassActionEventsQuery({
    offset,
    limit,
    ordering,
    tab: view || CLASS_ACTION_EVENTS_VIEW.UPCOMING,
  });

  const paginateProps = {
    onChangePage,
    onChangeRows,
    rowOptions: ROW_OPTIONS,
    count: data?.count || 0,
    page: Math.floor(offset / limit) + 1,
    rowsPerPage: limit,
  };

  const handleRowClick = (event: IClassActionEvent) => {
    const { id } = event;
    navigate(`/class-action-events/${id}/details`);
  };

  const handleSecondaryTabChange = (tab: string) => {
    const newParams = new URLSearchParams({
      view: tab,
    });
    setSearchParams(newParams);
  };

  const columns = useMemo(() => getColumns(view), [view]);
  const columnData = useMemo(() => {
    return getColumnData(view, {
      getSort,
      onOrderingChange,
    });
  }, [view, getSort, onOrderingChange]);
  const rowData = getRowData(view, data?.results || [], handleRowClick);

  return (
    <PageContainer>
      <Text variant="h4" sx={{ pb: 4, pt: 10, px: 10 }}>
        Class Action Events
      </Text>
      <ScrollContainer sx={{ pt: 4, pb: 10, pl: 10, pr: 6.25 }}>
        <Stack>
          <SecondaryTabs
            sx={{ mb: 8, mr: "auto" }}
            tabs={[
              {
                label: "Upcoming",
                value: CLASS_ACTION_EVENTS_VIEW.UPCOMING,
                icon:
                  view === CLASS_ACTION_EVENTS_VIEW.UPCOMING ? (
                    <WatchLater style={{ color: "#1968FF" }} />
                  ) : (
                    <WatchLaterOutlined />
                  ),
              },
              {
                label: "Active",
                value: CLASS_ACTION_EVENTS_VIEW.ACTIVE,
                icon:
                  view === CLASS_ACTION_EVENTS_VIEW.ACTIVE ? (
                    <Downloading style={{ color: "#1968FF" }} />
                  ) : (
                    <DownloadingOutlined />
                  ),
              },
              {
                label: "Recently completed",
                value: CLASS_ACTION_EVENTS_VIEW.RECENTLY_COMPLETED,
                icon:
                  view === CLASS_ACTION_EVENTS_VIEW.RECENTLY_COMPLETED ? (
                    <Verified style={{ color: "#1968FF" }} />
                  ) : (
                    <VerifiedOutlined />
                  ),
              },
              {
                label: "Historical",
                value: CLASS_ACTION_EVENTS_VIEW.HISTORICAL,
                icon:
                  view === CLASS_ACTION_EVENTS_VIEW.HISTORICAL ? (
                    <History style={{ color: "#1968FF" }} />
                  ) : (
                    <HistoryOutlined />
                  ),
              },
            ]}
            activeTab={view}
            clickHandler={handleSecondaryTabChange}
          />
        </Stack>
        <Table
          count={data?.count || 0}
          columnData={columnData}
          columns={columns}
          isFetching={isFetching}
          isLoading={isLoading}
          noResultsData={{
            title: "No Class Action Events found",
            subtitle:
              "Sorry, there are no Class Action Events that match your applied filters.",
          }}
          paginateProps={paginateProps}
          rows={rowData}
          tableMinWidth={1100}
          title={
            view === CLASS_ACTION_EVENTS_VIEW.UPCOMING
              ? "Upcoming Class Action Events"
              : view === CLASS_ACTION_EVENTS_VIEW.ACTIVE
              ? "Active Class Action Events"
              : view === CLASS_ACTION_EVENTS_VIEW.RECENTLY_COMPLETED
              ? "Recently Completed Class Action Events"
              : "Historical Class Action Events"
          }
          titleVariant="subtitle2"
        />
      </ScrollContainer>
    </PageContainer>
  );
}
